import { supabase } from '@/core/database/supabase';
import type { 
  ConstructionNCR, 
  NcrStats, 
  NcrFilters 
} from '../types/construction-ncr.types';

export const ConstructionNcrService = {
  /**
   * Danh sách báo cáo không phù hợp (NCR)
   */
  async getNcrs(companyId: string, filters: NcrFilters = {}): Promise<{ data: ConstructionNCR[], total: number }> {
    let query = supabase
      .from('construction_ncrs')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        detector:user_profiles!detected_by(id, full_name, avatar_url:avatar_path),
        assignee:user_profiles!assigned_to(id, full_name, avatar_url:avatar_path),
        responsible_team:teams(id, name, code)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.severity && filters.severity !== 'all') query = query.eq('severity_code', filters.severity);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.assignedTo) query = query.eq('assigned_to', filters.assignedTo);
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,ncr_code.ilike.%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as ConstructionNCR[],
      total: count || 0
    };
  },

  /**
   * Chi tiết NCR
   */
  async getNcrDetail(id: string): Promise<ConstructionNCR> {
    const { data, error } = await supabase
      .from('construction_ncrs')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        detector:user_profiles!detected_by(id, full_name, avatar_url:avatar_path),
        assignee:user_profiles!assigned_to(id, full_name, avatar_url:avatar_path),
        responsible_team:teams(id, name, code)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ConstructionNCR;
  },

  /**
   * Thống kê NCR
   */
  async getNcrStats(companyId: string, projectId?: string): Promise<NcrStats> {
    let query = supabase.from('construction_ncrs').select('status_code, severity_code, estimated_cost_impact, estimated_delay_days').eq('company_id', companyId);
    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) throw error;

    const openStatus = ['open', 'under_review', 'action_required', 'rework_in_progress', 'pending_verification'];
    const openCount = data.filter(i => openStatus.includes(i.status_code)).length;
    const criticalCount = data.filter(i => i.severity_code === 'critical' || i.severity_code === 'high').length;
    
    const costImpact = data.reduce((sum, i) => sum + (i.estimated_cost_impact || 0), 0);
    const delayImpact = data.reduce((sum, i) => sum + (i.estimated_delay_days || 0), 0);

    return {
      totalNcrs: data.length,
      openCount,
      criticalCount,
      avgResolutionTime: 0, // Simplified
      costImpact,
      delayImpact,
    };
  },

  /**
   * CRUD
   */
  async createNcr(data: Partial<ConstructionNCR>): Promise<ConstructionNCR> {
    const { data: result, error } = await supabase
      .from('construction_ncrs')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ConstructionNCR;
  },

  async updateNcr(id: string, updates: Partial<ConstructionNCR>): Promise<void> {
    const { error } = await supabase
      .from('construction_ncrs')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  }
};

